import { useState } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/Add'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
interface QuickAddChipsProps {
  title: string
  items: string[]
  /** Names already on this controller; those chips are shown as added. */
  existingNames?: string[]
  /** Stages the entry. Synchronous where the row is staged rather than written. */
  onAdd: (name: string) => void | Promise<void>
  disabled?: boolean
}

const normalise = (value: string) => value.trim().toLowerCase()

/**
 * SCRUM-1032 — one-click starters for a dataset.
 *
 * A new controller starts empty and the first entries are almost always the
 * same well-known names, so they are offered as chips rather than typed row by
 * row. A chip that is already on the controller shows as added and does
 * nothing, so the list doubles as a checklist of what has been entered.
 */
const QuickAddChips = ({
  title,
  items,
  existingNames = [],
  onAdd,
  disabled = false,
}: QuickAddChipsProps) => {
  const [pending, setPending] = useState<string | null>(null)
  const existing = new Set(existingNames.map(normalise))

  const handleAdd = async (name: string) => {
    if (disabled || pending || existing.has(normalise(name))) return
    setPending(name)
    try {
      await onAdd(name)
    } finally {
      setPending(null)
    }
  }

  return (
    <Stack sx={{ gap: '10px', pt: '14px', opacity: disabled ? 0.5 : 1 }}>
      <Typography
        sx={{
          // grey.700, not 600: this label is 11px, where the 600 (#7C7B7B)
          // reads at 4.22:1 on the light ground.
          color: 'grey.700',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>

      <Stack direction="row" sx={{ gap: '6px', flexWrap: 'wrap' }}>
        {items.map((item) => {
          const added = existing.has(normalise(item))
          const isPending = pending === item

          return (
            <Box
              key={item}
              component="button"
              type="button"
              disabled={disabled || added || !!pending}
              onClick={() => handleAdd(item)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                height: 26,
                px: '10px',
                borderRadius: '13px',
                border: '1px solid',
                borderColor: added ? 'transparent' : 'border.main',
                backgroundColor: added
                  ? (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,71,68,0.08)'
                  : 'transparent',
                color: added ? 'primary.main' : 'text.main',
                fontFamily: 'inherit',
                fontSize: '12px',
                cursor: added || disabled ? 'default' : 'pointer',
                transition: 'border-color 140ms ease, color 140ms ease',
                '&:hover:not(:disabled)': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              {isPending ? (
                <CircularProgress size={11} />
              ) : added ? (
                <CheckRoundedIcon sx={{ fontSize: 13 }} />
              ) : (
                <AddRoundedIcon sx={{ fontSize: 13 }} />
              )}
              {item}
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default QuickAddChips
